<template>
  <v-form ref="formRef" class="chiller-form method-form">
    <!-- Kältemaschine -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-snowflake</v-icon>
        <span>Kältemaschine</span>
      </div>

      <!-- Chiller Type -->
      <v-autocomplete
        v-model="form.chillerType"
        :items="chillerTypes"
        item-title="text"
        item-value="value"
        label="Typ der Kältemaschine"
        :error-messages="validationErrors?.chiller_type"
        :hint="hint.chiller_type"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.chiller_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.chiller_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.chiller_type"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('chiller_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- SEER / Jahresarbeitszahl -->
    <div v-if="isAC" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-chart-line</v-icon>
        <span>SEER / Jahresarbeitszahl</span>
      </div>

      <!-- SEER Known -->
      <v-checkbox
        v-model="form.knownSeer"
        :error-messages="validationErrors?.known_seer"
        label="SEER bekannt"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.known_seer" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.known_seer }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.known_seer"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('known_seer')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- Known SEER Value -->
      <v-text-field
        v-if="form.knownSeer"
        v-model.number="form.knownSeerValue"
        label="Jahresarbeitszahl (SEER)"
        :error-messages="validationErrors?.known_seer_value"
        :hint="hint.known_seer_value"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.known_seer_value" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.known_seer_value }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.known_seer_value"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('known_seer_value')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Nutzungsarten -->
    <div v-if="isKKM || (isAC && !Boolean(form.knownSeer))" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-format-list-checks</v-icon>
        <span>Nutzungsarten</span>
      </div>

      <!-- Usage Type 1 -->
      <div class="usage-type-row">
        <div class="usage-type-content">
          <ChillerUsageType
            :key="'usage1-' + form.chillerType"
            v-if="form.chillerType"
            v-model="form.usageType1"
            :field-errors="validationErrors?.usage_type_1 || {}"
            :chiller-type="form.chillerType"
            :free-cooling="form.freeCooling"
            :known-seer="form.knownSeer"
            :hint="hint"
            label="Nutzungsart 1"
            initiallyChecked
          />
        </div>

        <v-btn
          v-if="!reference && referenceData?.usage_type_1"
          icon
          class="usage-copy-button"
          title="Referenzwerte der Nutzungsart 1 übernehmen"
          @click="copyFromReference('usage_type_1')"
        >
          <v-icon>mdi-content-copy</v-icon>
        </v-btn>
      </div>

      <!-- Usage Type 2 -->
      <div class="usage-type-row">
        <div class="usage-type-content">
          <ChillerUsageType
            :key="'usage2-' + form.chillerType"
            v-if="form.chillerType"
            v-model="form.usageType2"
            :field-errors="validationErrors?.usage_type_2 || {}"
            :chiller-type="form.chillerType"
            :free-cooling="form.freeCooling"
            :known-seer="form.knownSeer"
            :disabled="!form.usageType1"
            :hint="hint"
            label="Nutzungsart 2"
          />
        </div>

        <v-btn
          v-if="!reference && referenceData?.usage_type_2"
          icon
          class="usage-copy-button"
          title="Referenzwerte der Nutzungsart 2 übernehmen"
          @click="copyFromReference('usage_type_2')"
        >
          <v-icon>mdi-content-copy</v-icon>
        </v-btn>
      </div>

      <!-- Usage Type 3 -->
      <div class="usage-type-row">
        <div class="usage-type-content">
          <ChillerUsageType
            :key="'usage3-' + form.chillerType"
            v-if="form.chillerType"
            v-model="form.usageType3"
            :field-errors="validationErrors?.usage_type_3 || {}"
            :chiller-type="form.chillerType"
            :free-cooling="form.freeCooling"
            :known-seer="form.knownSeer"
            :disabled="!form.usageType2"
            :hint="hint"
            label="Nutzungsart 3"
          />
        </div>

        <v-btn
          v-if="!reference && referenceData?.usage_type_3"
          icon
          class="usage-copy-button"
          title="Referenzwerte der Nutzungsart 3 übernehmen"
          @click="copyFromReference('usage_type_3')"
        >
          <v-icon>mdi-content-copy</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Leistungsdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-lightning-bolt-outline</v-icon>
        <span>Leistungsdaten</span>
      </div>

      <!-- Nominal Cooling Power -->
      <v-text-field
        v-model.number="form.nominalCoolingPower"
        label="Nennkälteleistung"
        suffix="kW"
        :error-messages="validationErrors?.nominal_cooling_power"
        :hint="hint.nominal_cooling_power"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.nominal_cooling_power" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.nominal_cooling_power }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.nominal_cooling_power"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('nominal_cooling_power')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Nominal Electrical Driving Power -->
      <v-text-field
        v-if="isKKM"
        v-model.number="form.nominalElectricalDrivingPower"
        label="Elektrische Nennantriebsleistung"
        suffix="kW"
        :error-messages="validationErrors?.nominal_electrical_driving_power"
        :hint="hint.nominal_electrical_driving_power"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.nominal_electrical_driving_power" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.nominal_electrical_driving_power }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.nominal_electrical_driving_power"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('nominal_electrical_driving_power')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Electric Energy Consumption -->
      <v-text-field
        v-if="reference && (isKKM || isAC)"
        v-model.number="form.finalElectricEnergyConsumption"
        label="Jährlicher elektrischer Energiebedarf"
        suffix="kWh/a"
        :error-messages="validationErrors?.final_electric_energy_consumption"
        :hint="hint.final_electric_energy_consumption"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.final_electric_energy_consumption" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.final_electric_energy_consumption }}</span>
          </v-tooltip>
        </template>
      </v-text-field>
    </div>

    <!-- Betriebsdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betriebsdaten</span>
      </div>

      <!-- Uptime -->
      <v-text-field
        v-model.number="form.uptime"
        label="Jährliche Betriebsstunden"
        suffix="h/a"
        :error-messages="validationErrors?.uptime"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Rückkühlung / Teillast -->
    <div v-if="isKKM || (isAC && !form.knownSeer)" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-fan</v-icon>
        <span>Rückkühlung und Teillast</span>
      </div>

      <!-- Cooling Type -->
      <v-autocomplete
        v-if="isKKM"
        v-model="form.coolingType"
        :items="coolingTypes"
        item-title="text"
        item-value="value"
        :error-messages="validationErrors?.cooling_type"
        label="Rückkühlung"
        :hint="hint.cooling_type"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.cooling_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.cooling_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.cooling_type"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('cooling_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- AC Type -->
      <v-autocomplete
        v-if="isAC && !form.knownSeer"
        v-model="form.acType"
        :items="acTypes"
        item-title="text"
        item-value="value"
        :error-messages="validationErrors?.ac_type"
        label="Anlagensystem"
        :hint="hint.ac_type"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.ac_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.ac_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.ac_type"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('ac_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Part Load Control -->
      <v-autocomplete
        v-if="isKKM || (isAC && !form.knownSeer)"
        v-model="form.partLoadControl"
        :items="partLoadChoices"
        item-title="text"
        item-value="value"
        label="Art der Teillastregelung"
        :error-messages="validationErrors?.part_load_control"
        :hint="hint.part_load_control"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.part_load_control" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.part_load_control }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.part_load_control"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('part_load_control')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Water-cooled inlet -->
      <v-autocomplete
        v-if="isWaterKKM"
        v-model="form.coolantInlet"
        :items="coolantInlets"
        item-title="text"
        item-value="value"
        label="Kühlwassereintritt Kältemaschine"
        :error-messages="validationErrors?.coolant_inlet"
        :hint="hint.coolant_inlet"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.coolant_inlet" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.coolant_inlet }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.coolant_inlet"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('coolant_inlet')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Air-cooled mode -->
      <v-autocomplete
        v-if="isAirKKM"
        v-model="form.coolerMode"
        :items="coolerModes"
        item-title="text"
        item-value="value"
        label="Art der Rückkühlung"
        :error-messages="validationErrors?.cooler_mode"
        :hint="hint.cooler_mode"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.cooler_mode" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.cooler_mode }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.cooler_mode"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('cooler_mode')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Cooler Type -->
      <v-autocomplete
        v-if="isWaterKKM || isAirKKM"
        v-model="form.coolerType"
        :items="coolerTypes"
        item-title="text"
        item-value="value"
        label="Art des Rückkühlers"
        :error-messages="validationErrors?.cooler_type"
        :hint="hint.cooler_type"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.cooler_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.cooler_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.cooler_type"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('cooler_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Sorption -->
    <div v-if="isSorb" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-fire-circle</v-icon>
        <span>Sorptionskälte</span>
      </div>

      <v-text-field
        v-model.number="form.nominalHeatingPower"
        label="Nennheizwärmeleistung"
        suffix="kW"
        :error-messages="validationErrors?.nominal_heating_power"
        :hint="hint.nominal_heating_power"
        validate-on-blur
        class="mb-3"
      />

      <v-text-field
        v-if="reference"
        v-model.number="form.finalThermalEnergyConsumption"
        label="Jährlicher thermischer Energiebedarf"
        suffix="kWh/a"
        :error-messages="validationErrors?.final_thermal_energy_consumption"
        :hint="hint.final_thermal_energy_consumption"
        validate-on-blur
        class="mb-3"
      />

      <v-autocomplete
        v-model="form.coolantSolventPair"
        :items="coolantSolventPairs"
        item-title="text"
        item-value="value"
        :error-messages="validationErrors?.coolant_solvent_pair"
        label="Kältemittel/Lösungsmittel-Paar"
        :hint="hint.coolant_solvent_pair"
        validate-on-blur
        class="mb-3"
      />
    </div>

    <!-- Freie Kühlung -->
    <div v-if="isKKM" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-snowflake-thermometer</v-icon>
        <span>Freie Kühlung</span>
      </div>

      <v-checkbox
        v-model="form.freeCooling"
        label="Freie Kühlung"
        :error-messages="validationErrors?.free_cooling"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.free_cooling" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.free_cooling }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.free_cooling"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('free_cooling')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <v-autocomplete
        v-if="form.freeCooling"
        v-model="form.freeCoolingMode"
        :items="freeCoolingModes"
        item-title="text"
        item-value="value"
        label="Betriebsweise der freien Kühlung"
        :error-messages="validationErrors?.free_cooling_mode"
        :hint="hint.free_cooling_mode"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.free_cooling_mode" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.free_cooling_mode }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>
    </div>
  </v-form>
</template>


<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import api from '@/plugins/axios'
import ChillerUsageType from '@/components/methods/ChillerUsageType.vue'
import { djangoChoicesToVuetify } from '@/utils/index.js'

/* ---------------- PROPS ---------------- */
const props = defineProps({
  modelValue: Object,
  reference: Boolean,
  planned: Boolean,
  otherData: Object,
  sharedData: Object,
  validationErrors: Object,
  methodValue: String
})

const emit = defineEmits(['update:modelValue'])

/* ---------------- FIELD MAP ---------------- */
const fields = {
  usageType1: 'usage_type_1',
  usageType2: 'usage_type_2',
  usageType3: 'usage_type_3',
  chillerType: 'chiller_type',
  nominalCoolingPower: 'nominal_cooling_power',
  nominalElectricalDrivingPower: 'nominal_electrical_driving_power',
  finalElectricEnergyConsumption: 'final_electric_energy_consumption',
  coolingType: 'cooling_type',
  partLoadControl: 'part_load_control',
  coolantInlet: 'coolant_inlet',
  coolerMode: 'cooler_mode',
  coolerType: 'cooler_type',
  nominalHeatingPower: 'nominal_heating_power',
  finalThermalEnergyConsumption: 'final_thermal_energy_consumption',
  coolantSolventPair: 'coolant_solvent_pair',
  uptime: 'uptime',
  knownSeer: 'known_seer',
  knownSeerValue: 'known_seer_value',
  acType: 'ac_type',
  freeCooling: 'free_cooling',
  freeCoolingMode: 'free_cooling_mode'
}

/* ---------------- useBaseForm ---------------- */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: { knownSeer: false, freeCooling: false },
  reference: computed(() => props.reference),
  planned: computed(() => props.planned),
  shared: computed(() => false),
  otherData: computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (e, v) => emit(e, v)
})

/* ---------------- FORM REF ---------------- */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})


/* ---------------- VISIBILITY LOGIC ---------------- */
const isKKM = computed(() => form.chillerType === 1)
const isSorb = computed(() => form.chillerType === 2)
const isAC = computed(() => form.chillerType === 3)

const isWaterKKM = computed(() => isKKM.value && form.coolingType === 1)
const isAirKKM = computed(() => isKKM.value && form.coolingType === 2)

/* ---------------- API-LOADED OPTIONS ---------------- */
const chillerTypes = ref([])
const coolingTypes = ref([])
const coolerModes = ref([])
const coolerTypes = ref([])
const coolantInlets = ref([])
const coolantSolventPairs = ref([])
const partLoadChoices = ref([])
const acTypes = ref([])
const freeCoolingModes = ref([])

/* ---------------- API FETCHERS (FIXED) ---------------- */
async function fetchChillerTypes() {
  const { data } = await api.get('/methods/chiller/chiller-types/')
  chillerTypes.value = djangoChoicesToVuetify(data)
}

async function fetchCoolingTypes() {
  const { data } = await api.get('/methods/chiller/cooling-types/')
  coolingTypes.value = djangoChoicesToVuetify(data)
}

async function fetchCoolerModes() {
  const { data } = await api.get('/methods/chiller/cooler-modes/')
  coolerModes.value = djangoChoicesToVuetify(data)
}

async function fetchCoolerTypes() {
  const { data } = await api.get('/methods/chiller/cooler-types/', {
    params: { cooling_type: form.coolingType, cooler_mode: form.coolerMode }
  })
  coolerTypes.value = djangoChoicesToVuetify(data)
  if (coolerTypes.value.length === 1) {
    form.coolerType = coolerTypes.value[0].value
  }
}

async function fetchCoolantInlets() {
  const { data } = await api.get('/methods/chiller/coolant-inlet/')
  coolantInlets.value = djangoChoicesToVuetify(data)
}

async function fetchCoolantSolventPairs() {
  const { data } = await api.get('/methods/chiller/coolant-solvent-pairs/')
  coolantSolventPairs.value = djangoChoicesToVuetify(data)
}

async function fetchAcTypes() {
  const { data } = await api.get('/methods/chiller/ac-types/')
  acTypes.value = djangoChoicesToVuetify(data)
}

async function fetchPartLoadChoices() {
  const { data } = await api.get('/methods/chiller/part-load-control/', {
    params: {
      chiller_type: form.chillerType,
      cooling_type: form.coolingType,
      ac_type: form.acType
    }
  })
  partLoadChoices.value = djangoChoicesToVuetify(data)
  if (partLoadChoices.value.length === 1) {
    form.partLoadControl = partLoadChoices.value[0].value
  }
}

async function fetchFreeCoolingModes() {
  const { data } = await api.get('/methods/chiller/free-cooling-modes/', {
    params: { cooling_type: form.coolingType }
  })
  freeCoolingModes.value = djangoChoicesToVuetify(data)
  form.freeCoolingMode = null
  if (freeCoolingModes.value.length === 1) {
    form.freeCoolingMode = freeCoolingModes.value[0].value
  }
}

/* ---------------- WATCHERS ---------------- */
watch(isKKM, (v) => {
  if (!v) {
    form.nominalElectricalDrivingPower = null
    form.finalElectricEnergyConsumption = null
    form.coolingType = null
    form.partLoadControl = null
    form.coolantInlet = null
    form.coolerMode = null
    form.coolerType = null
  }
})

watch(isSorb, (v) => {
  if (!v) {
    form.nominalHeatingPower = null
    form.finalThermalEnergyConsumption = null
    form.coolantSolventPair = null
  }
})


watch(isSorb, (v) => {
  if (v) {
    form.usageType1 = null
    form.usageType2 = null
    form.usageType3 = null
  }
})


watch(isAC, (v) => {
  if (!v) {
    form.knownSeer = null
    form.acType = null
    form.knownSeerValue = null
  }
})

watch(() => form.coolingType, async (v) => {
  if (v != null) {
    await fetchPartLoadChoices()
    await fetchFreeCoolingModes()
  }
  await fetchCoolerTypes()
}, { immediate: true })

watch(() => form.chillerType, (v) => {
  if (v != null && form.coolingType != null) {
    fetchPartLoadChoices()
  }
})

watch(() => form.acType, (v) => {
  if (v != null) {
    fetchPartLoadChoices()
  }
})

watch(() => form.coolerMode, (v) => {
  if (v != null) {
    fetchCoolerTypes()
  }
})

// watch(
//   () => form.knownSeer,
//   (known) => {
//     if (!isAC.value) return

//     if (known) {
//       // These are invalid when SEER is known
//       form.acType = null
//       form.partLoadControl = null

//       // Usage types are not used in backend validation when known_seer is true
//       form.usageType1 = null
//       form.usageType2 = null
//       form.usageType3 = null
//     } else {
//       // Backend rejects known_seer_value when SEER is not known
//       form.knownSeerValue = null
//     }
//   }
// )

/* ---------------- INITIALIZE ---------------- */
onMounted(() => {
  fetchChillerTypes()
  fetchCoolingTypes()
  fetchCoolerModes()
  fetchCoolerTypes()
  fetchCoolantInlets()
  fetchCoolantSolventPairs()
  fetchAcTypes()
  fetchHints()
})
</script>

<style scoped lang="scss">
.chiller-form {
  width: 100%;
}

/* Field styling */
.chiller-form :deep(.v-field) {
  border-radius: 18px;
}

.chiller-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkboxes */
.chiller-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested/dependent fields */
.chiller-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint/copy icons */
.chiller-form :deep(.v-input__append),
.chiller-form :deep(.v-field__append-inner) {
  align-items: center;
}

.chiller-form :deep(.v-input__append .v-icon),
.chiller-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.chiller-form :deep(.v-input__append .v-btn),
.chiller-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.chiller-form :deep(.v-input__append .v-btn:hover),
.chiller-form :deep(.v-field__append-inner .v-btn:hover),
.usage-copy-button:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Usage type layout */
.usage-type-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.usage-type-content {
  flex: 1 1 auto;
  min-width: 0;
}

.usage-copy-button {
  margin-top: 0.4rem;
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

/* Disabled fields */
.chiller-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .usage-type-row {
    flex-direction: column;
  }

  .usage-copy-button {
    margin-top: 0;
  }

  .chiller-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>